/**
 * Full-bleed overlay shown when autoplay is blocked by the browser.
 *
 * Rendered by WS-5's RoomPage when `player.play()` rejects with NotAllowedError
 * (the browser requires a user gesture before playback can start). Clicking
 * the overlay re-attempts playback via the `onJoin` callback and clears the
 * overlay. This is a plain React component (not a Video.js component).
 *
 * @packageDocumentation
 */
import type { ReactElement, KeyboardEvent } from 'react';

/** Props for the join-playback overlay. */
export interface JoinPlaybackOverlayProps {
  /** Called when the viewer clicks or activates the overlay to start playback. */
  onJoin: () => void;
  /** Whether the overlay should be visible. */
  visible: boolean;
}

/**
 * Full-bleed click target prompting the viewer to join synchronized playback.
 *
 * Renders nothing when `visible` is false. When visible, the entire surface is
 * a button; clicking it or pressing Enter/Space invokes `onJoin`.
 */
export function JoinPlaybackOverlay({
  onJoin,
  visible,
}: JoinPlaybackOverlayProps): ReactElement | null {
  if (!visible) return null;

  const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>): void => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      onJoin();
    }
  };

  return (
    <div
      className="syncreel-join-overlay"
      role="button"
      tabIndex={0}
      aria-label="Click to join playback"
      onClick={onJoin}
      onKeyDown={handleKeyDown}
      style={{
        position: 'absolute',
        inset: 0,
        zIndex: 90,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 14,
        background: 'rgba(10, 10, 10, 0.86)',
        cursor: 'pointer',
        color: 'var(--color-text, #f5f5f5)',
        fontFamily: 'var(--font-sans, system-ui, sans-serif)',
      }}
    >
      <div
        className="syncreel-join-overlay__icon"
        style={{
          width: 60,
          height: 60,
          borderRadius: '50%',
          background: 'linear-gradient(150deg, #f0222d, #b20710)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          boxShadow: '0 12px 32px -8px rgba(229, 9, 20, 0.6)',
        }}
      >
        <div
          style={{
            width: 0,
            height: 0,
            borderTop: '11px solid transparent',
            borderBottom: '11px solid transparent',
            borderLeft: '17px solid #fff',
            marginLeft: 4,
          }}
        />
      </div>
      <p
        style={{
          margin: 0,
          fontSize: '15px',
          fontWeight: 700,
        }}
      >
        Click to join playback
      </p>
    </div>
  );
}
